import React, { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import Button from '../../buttons/Button';
import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  country?: string;
  city?: string;
  address?: string;
  onChange: (latitude: number, longitude: number) => void;
}

// Shown until a pin is placed: the whole of Bulgaria
const DEFAULT_CENTER: L.LatLngTuple = [42.7339, 25.4858];
const DEFAULT_ZOOM = 7;
const PIN_ZOOM = 17;

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

// Leaflet resolves its default marker images relative to the CSS file, which breaks once bundled
const pinIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
});

const hasCoordinates = (latitude?: number, longitude?: number) =>
  !!Number(latitude) && !!Number(longitude);

const roundCoordinate = (value: number) => Number(value.toFixed(6));

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  country,
  city,
  address,
  onChange,
}) => {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [searching, setSearching] = useState(false);

  const placeMarker = useCallback((position: L.LatLngExpression) => {
    const map = mapRef.current;
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.setLatLng(position);
      return;
    }

    const marker = L.marker(position, { icon: pinIcon, draggable: true });
    marker.on('dragend', () => {
      const { lat, lng } = marker.getLatLng();
      onChangeRef.current(roundCoordinate(lat), roundCoordinate(lng));
    });
    marker.addTo(map);
    markerRef.current = marker;
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const map = L.map(containerRef.current).setView(
      DEFAULT_CENTER,
      DEFAULT_ZOOM
    );

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    map.on('click', (e: L.LeafletMouseEvent) => {
      onChangeRef.current(
        roundCoordinate(e.latlng.lat),
        roundCoordinate(e.latlng.lng)
      );
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Keep the pin in sync with the form value (loaded complex, search result, click, drag)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !hasCoordinates(latitude, longitude)) return;

    const position: L.LatLngTuple = [Number(latitude), Number(longitude)];
    const isFirstPin = !markerRef.current;

    placeMarker(position);

    if (isFirstPin) {
      map.setView(position, PIN_ZOOM);
    } else if (!map.getBounds().contains(position)) {
      map.panTo(position);
    }
  }, [latitude, longitude, placeMarker]);

  const searchAddress = async () => {
    if (!address?.trim() || !city?.trim()) {
      toast.error(t(COMMON_TRANSLATE_KEYS.LOCATION_FILL_ADDRESS));
      return;
    }

    setSearching(true);

    try {
      const params = { format: 'jsonv2', limit: 1 };

      // Structured search first, then free text, then just the city so the map at least gets close
      const queries = [
        { ...params, street: address, city, country },
        { ...params, q: [address, city, country].filter(Boolean).join(', ') },
        { ...params, city, country },
      ];

      for (const query of queries) {
        const { data } = await axios.get(NOMINATIM_URL, { params: query });

        if (data?.length) {
          const lat = roundCoordinate(Number(data[0].lat));
          const lng = roundCoordinate(Number(data[0].lon));

          onChangeRef.current(lat, lng);
          mapRef.current?.setView([lat, lng], PIN_ZOOM);
          toast.info(t(COMMON_TRANSLATE_KEYS.LOCATION_CHECK_PIN));
          return;
        }
      }

      toast.error(t(COMMON_TRANSLATE_KEYS.LOCATION_NOT_FOUND));
    } catch (error) {
      toast.error(t(COMMON_TRANSLATE_KEYS.LOCATION_NOT_FOUND));
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="w-full text-mainWhite">
      <p>{t(COMMON_TRANSLATE_KEYS.LOCATION_ON_MAP)}</p>
      <p className="text-textGray text-sm mt-1">
        {t(COMMON_TRANSLATE_KEYS.LOCATION_HINT)}
      </p>
      <div className="mt-3">
        <Button
          textColor="text-mainBlue"
          border="border"
          borderColor="border-mainBlue"
          onClick={searchAddress}
          isLoading={searching}
          loadingText={`${t(COMMON_TRANSLATE_KEYS.LOCATION_SEARCHING)}...`}
        >
          {t(COMMON_TRANSLATE_KEYS.LOCATION_FIND_ADDRESS)}
        </Button>
      </div>
      <div
        ref={containerRef}
        className="mt-3 w-full h-80 rounded-2xl overflow-hidden z-0"
      />
      <p className="text-textGray text-sm mt-2">
        {hasCoordinates(latitude, longitude)
          ? `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`
          : t(COMMON_TRANSLATE_KEYS.LOCATION_NOT_SET)}
      </p>
    </div>
  );
};

export default LocationPicker;
